import React, { useRef, useEffect, useState, useCallback } from "react";
import "./RightSideBar.scss";

function RightSideBar({
  isVisible,
  content,
  currentTask,
  fetchTasks,
  hideRightSideBar,
}) {
  const sidebarRef = useRef(null);
  const contentRef = useRef(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const pendingCloseRef = useRef(false);

  // Function to check for unsaved changes and handle close
  const handleClose = useCallback(() => {
    if (
      contentRef.current &&
      typeof contentRef.current.hasUnsavedChanges === "function" &&
      contentRef.current.hasUnsavedChanges()
    ) {
      setShowConfirmDialog(true);
    } else {
      hideRightSideBar();
    }
  }, [hideRightSideBar]);

  // Handle save and close
  const handleSaveAndClose = async () => {
    if (
      contentRef.current &&
      typeof contentRef.current.saveChanges === "function"
    ) {
      await contentRef.current.saveChanges();
    }
    setShowConfirmDialog(false);
    hideRightSideBar();
  };

  // Handle discard and close
  const handleDiscardAndClose = () => {
    setShowConfirmDialog(false);
    hideRightSideBar();
  };

  // Handle cancel (keep editing)
  const handleCancelClose = () => {
    setShowConfirmDialog(false);
    pendingCloseRef.current = false;
  };

  // Handle click outside to close sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isVisible &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        // Check if click is on an element that opens the sidebar (e.g., task row elements)
        // to avoid closing immediately when clicking on a task
        const isTaskElement = event.target.closest(
          ".task, .task-row, .task-title, .open_in_new, .links, .description",
        );
        if (!isTaskElement) {
          handleClose();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, handleClose]);

  // Clone the content and inject additional props if it's a React element
  const contentWithProps = React.isValidElement(content)
    ? React.cloneElement(content, {
        ...content.props,
        ref: contentRef,
        currentTask,
        fetchTasks,
        hideRightSideBar: hideRightSideBar, // Direct close without confirmation
        hideRightSideBarWithCheck: handleClose, // Close with unsaved changes check
      })
    : content;

  return (
    <>
      <div
        ref={sidebarRef}
        className={`right-sidebar ${isVisible ? "visible" : "hidden"}`}
      >
        {currentTask && (
          <div className="right-sidebar-header">
            <span className="right-sidebar-title">
              Task: {currentTask.title}
            </span>
            <button className="close-button" onClick={handleClose}>
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "22px" }}
              >
                close
              </span>
            </button>
          </div>
        )}
        {contentWithProps}
      </div>

      {showConfirmDialog && (
        <div className="right-sidebar-confirm-overlay">
          <div className="right-sidebar-confirm-dialog">
            <p>You have unsaved changes. Do you want to save them?</p>
            <div className="confirm-buttons">
              <button className="cancel-btn" onClick={handleCancelClose}>
                Cancel
              </button>
              <button className="discard-btn" onClick={handleDiscardAndClose}>
                Discard
              </button>
              <button className="save-btn" onClick={handleSaveAndClose}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RightSideBar;
